import React, { memo, useCallback, useMemo } from "react";

import {
  ComparisonFlamebearer,
  ComparisonFlamebearerNode,
  FlamebearerCall,
  ProfileType
} from "@platformsh/observability-graph/ContinuousProfiling";

import Dialog from "ds/Dialog";
import {
  ComparisonSide,
  Range
} from "src/organization/pages/project/environment/metrics/profiling/components/Conprof/types";

import ComparisonFlamebearerCallTooltip from "../ComparisonFlamebearerCallTooltip";
import FlamebearerCallTooltip from "../FlamebearerCallTooltip";
import { getSimpleDelta } from "../utils";

import * as S from "./styles";

type Props = {
  node: ComparisonFlamebearerNode;
  flamebearer: ComparisonFlamebearer;
  range: Range;
  profileType: ProfileType;

  highlighted: boolean;
  onCallHighlighted: (call: FlamebearerCall | undefined) => void;

  side: ComparisonSide;
};

const ComparisonFlamebearerGraphNode = ({
  node,
  flamebearer,
  range,
  profileType,
  highlighted,
  onCallHighlighted,
  side
}: Props) => {
  const handleEnter = useCallback(() => {
    onCallHighlighted(node.call);
  }, [node.call, onCallHighlighted]);
  const handleLeave = useCallback(() => {
    onCallHighlighted(undefined);
  }, [onCallHighlighted]);

  const { left, right } = useMemo(() => {
    const size = range.to - range.from;
    const left = Math.max(0, node.start - range.from);
    const right = Math.max(0, range.to - node.end);

    return { left: left / size, right: right / size };
  }, [node.end, node.start, range]);

  const delta = useMemo(
    () => getSimpleDelta(node.compared?.delta.value),
    [node.compared?.delta.value]
  );

  return (
    <Dialog
      align="center"
      size="large"
      trigger={
        <S.Node
          level={node.level}
          highlighted={highlighted}
          style={{
            left: `${100 * left}%`,
            right: `${100 * right}%`
          }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <S.ComparisonNodeBackground
            style={
              side === "A"
                ? {}
                : { opacity: Math.abs(node.compared?.delta.impact ?? 1) }
            }
            delta={delta}
            side={side}
          />
          <S.NodeLabel>{node.call.label}</S.NodeLabel>
        </S.Node>
      }
      link={
        side === "A" || !node.compared ? (
          <FlamebearerCallTooltip
            callMetrics={node}
            profileType={profileType}
          />
        ) : (
          <ComparisonFlamebearerCallTooltip
            node={node}
            flamebearer={flamebearer}
            profileType={profileType}
          />
        )
      }
    />
  );
};

export default memo(ComparisonFlamebearerGraphNode);
